import { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '../../../../../components/forms/Input';
import { Select } from '../../../../../components/forms/Select';
import { RadioGroup } from '../../../../../components/forms/RadioGroup';
import { RangeSlider } from '../../../../../components/forms/RangeSlider';
import { Tabs } from '../../../../../components/forms/Tabs';
import { MoneyInput } from '../../../../../components/forms/MoneyInput';
import { Button } from '../../../../../components/Button';
import { normalizeCpfCnpj } from '../../../../../utils/mask';
import { maskMobile } from '../../phone';
import {
  graceOptions,
  INDEX_OPTIONS,
  INTEREST_TABLE_OPTIONS,
  MONETARY_CORRECTION_OPTIONS,
  PERSON_TYPE_OPTIONS,
  UF_OPTIONS,
} from '../../constants';
import {
  buildSimulationSchema,
  findConditional,
  SimulationFormValues,
} from '../../schemas';
import { ICashMeLimiters } from '../../types';
import { WizardShell, WizardSection } from '../WizardShell/WizardShell';
import styles from '../WizardShell/WizardShell.module.css';

interface ISimulationForm {
  limiters: ICashMeLimiters;
  defaultName: string;
  defaultEmail: string;
  defaultPhone: string;
  initialValues?: SimulationFormValues | null;
  isSubmitting: boolean;
  onSubmit: (
    values: SimulationFormValues
  ) => Promise<ISimulationFieldError | void>;
}

export interface ISimulationFieldError {
  field: keyof SimulationFormValues;
  message: string;
}

export function SimulationForm({
  limiters,
  defaultName,
  defaultEmail,
  defaultPhone,
  initialValues,
  isSubmitting,
  onSubmit,
}: ISimulationForm) {
  const schema = useMemo(() => buildSimulationSchema(limiters), [limiters]);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<SimulationFormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      personType: 'natural',
      name: defaultName,
      email: defaultEmail,
      phone: maskMobile(defaultPhone),
      document: '',
      birthDate: '',
      propertyState: '',
      interestTable: 'PRICE',
      installmentPeriod: 120,
      gracePeriod: 1,
      monetaryCorrection: 'POST_FIXED',
      index: 'IPCA',
      ...initialValues,
    },
  });

  const personType = watch('personType');
  const interestTable = watch('interestTable');
  const monetaryCorrection = watch('monetaryCorrection');
  const index = watch('index');
  const installmentPeriod = watch('installmentPeriod');

  /* Only combinations CashMe lists in `conditionals` are offered; each choice
     narrows the next (table → correction → index). */
  const { conditionals } = limiters;
  const tableOptions = INTEREST_TABLE_OPTIONS.filter((option) =>
    conditionals.some((item) => item.interestTable === option.value)
  );
  const correctionOptions = MONETARY_CORRECTION_OPTIONS.filter((option) =>
    conditionals.some(
      (item) =>
        item.interestTable === interestTable &&
        item.monetaryCorrection === option.value
    )
  );
  const indexOptions = INDEX_OPTIONS.filter((option) =>
    conditionals.some(
      (item) =>
        item.interestTable === interestTable &&
        item.monetaryCorrection === monetaryCorrection &&
        item.indexType === option.value
    )
  );
  const conditional = findConditional(
    limiters,
    interestTable,
    monetaryCorrection,
    index
  );
  const termMin =
    conditional?.minTerm ?? Math.min(...conditionals.map((c) => c.minTerm));
  const termMax =
    conditional?.maxTerm ?? Math.max(...conditionals.map((c) => c.maxTerm));
  const stateOptions = UF_OPTIONS.filter(
    (option) => !limiters.statesNotAccepted.includes(option.value)
  );

  useEffect(() => {
    if (
      tableOptions.length &&
      !tableOptions.some((option) => option.value === interestTable)
    ) {
      setValue('interestTable', tableOptions[0].value as any);
    }
  }, [tableOptions, interestTable, setValue]);

  useEffect(() => {
    if (
      correctionOptions.length &&
      !correctionOptions.some((option) => option.value === monetaryCorrection)
    ) {
      setValue('monetaryCorrection', correctionOptions[0].value as any);
    }
  }, [correctionOptions, monetaryCorrection, setValue]);

  /* PRE_FIXED combinations have no index — it must be cleared, not sent. */
  useEffect(() => {
    if (!indexOptions.length) {
      if (index !== undefined) setValue('index', undefined);
    } else if (!indexOptions.some((option) => option.value === index)) {
      setValue('index', indexOptions[0].value as any);
    }
  }, [indexOptions, index, setValue]);

  useEffect(() => {
    if (installmentPeriod > termMax) setValue('installmentPeriod', termMax);
    if (installmentPeriod < termMin) setValue('installmentPeriod', termMin);
  }, [termMin, termMax, installmentPeriod, setValue]);

  return (
    <WizardShell
      title="Simule seu empréstimo com garantia de imóvel"
      helper="Aqui seu imóvel dá crédito para você e para sua empresa. Preencha os dados ao lado para ver as condições."
    >
      <form
        onSubmit={handleSubmit(async (values) => {
          const fieldError = await onSubmit(values);
          if (fieldError) {
            setError(
              fieldError.field,
              { type: 'server', message: fieldError.message },
              { shouldFocus: true }
            );
          }
        })}
        noValidate
      >
        <WizardSection number={1} title="Sobre o crédito">
          <div className={styles.field}>
            <Controller
              control={control}
              name="loanAmount"
              render={({ field }) => (
                <MoneyInput
                  name="loanAmount"
                  label="De quanto você precisa?"
                  placeholder="R$ 100.000"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  errors={errors.loanAmount?.message}
                />
              )}
            />
          </div>

          <div className={styles.field}>
            <Controller
              control={control}
              name="personType"
              render={({ field }) => (
                <RadioGroup
                  name="personType"
                  label="A contratação de crédito será feita por:"
                  options={PERSON_TYPE_OPTIONS}
                  value={field.value}
                  onChange={field.onChange}
                  errors={errors.personType?.message}
                />
              )}
            />
          </div>
        </WizardSection>

        <WizardSection number={2} title="Sobre o imóvel">
          <div className={styles.row}>
            <div className={styles.field}>
              <Controller
                control={control}
                name="propertyAmount"
                render={({ field }) => (
                  <MoneyInput
                    name="propertyAmount"
                    label="Valor aproximado do imóvel"
                    placeholder="R$ 250.000"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    errors={errors.propertyAmount?.message}
                  />
                )}
              />
            </div>

            <div className={styles.field}>
              <Select
                label="Estado do imóvel"
                placeholder="Selecione"
                options={stateOptions}
                errors={errors.propertyState?.message}
                {...register('propertyState')}
              />
            </div>
          </div>
        </WizardSection>

        <WizardSection number={3} title="Condições">
          <div className={styles.field}>
            <Controller
              control={control}
              name="interestTable"
              render={({ field }) => (
                <Tabs
                  options={tableOptions}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div className={styles.field}>
            <Controller
              control={control}
              name="installmentPeriod"
              render={({ field }) => (
                <RangeSlider
                  name="installmentPeriod"
                  label="Prazo de pagamento"
                  min={termMin}
                  max={termMax}
                  value={field.value}
                  onChange={field.onChange}
                  suffix="meses"
                  errors={errors.installmentPeriod?.message}
                />
              )}
            />
          </div>

          <div className={indexOptions.length ? styles.rowThree : styles.row}>
            <div className={styles.field}>
              <Controller
                control={control}
                name="gracePeriod"
                render={({ field }) => (
                  <RadioGroup
                    name="gracePeriod"
                    label="Carência"
                    column
                    options={graceOptions(limiters.graceMonthsTerm)}
                    value={String(field.value)}
                    onChange={(event) =>
                      field.onChange(Number(event.target.value))
                    }
                    errors={errors.gracePeriod?.message}
                  />
                )}
              />
            </div>

            <div className={styles.field}>
              <Controller
                control={control}
                name="monetaryCorrection"
                render={({ field }) => (
                  <RadioGroup
                    name="monetaryCorrection"
                    label="Correção"
                    column
                    options={correctionOptions}
                    value={field.value}
                    onChange={field.onChange}
                    errors={errors.monetaryCorrection?.message}
                  />
                )}
              />
            </div>

            {indexOptions.length > 0 && (
              <div className={styles.field}>
                <Controller
                  control={control}
                  name="index"
                  render={({ field }) => (
                    <RadioGroup
                      name="index"
                      label="Índice"
                      column
                      options={indexOptions}
                      value={field.value}
                      onChange={field.onChange}
                      errors={errors.index?.message}
                    />
                  )}
                />
              </div>
            )}
          </div>
        </WizardSection>

        <WizardSection number={4} title="Seus dados">
          <div className={styles.field}>
            <Input
              type="text"
              label="Nome completo"
              placeholder="Nome completo"
              autoComplete="off"
              errors={errors.name?.message}
              {...register('name')}
            />
          </div>

          <div className={styles.row}>
            <div className={styles.field}>
              <Input
                type="text"
                label={personType === 'legal' ? 'CNPJ' : 'CPF'}
                placeholder={
                  personType === 'legal'
                    ? '00.000.000/0000-00'
                    : '000.000.000-00'
                }
                autoComplete="off"
                errors={errors.document?.message}
                {...register('document', {
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                    setValue('document', normalizeCpfCnpj(event.target.value)),
                })}
              />
            </div>

            {personType === 'natural' && (
              <div className={styles.field}>
                <Input
                  type="date"
                  label="Data de nascimento"
                  errors={errors.birthDate?.message}
                  {...register('birthDate')}
                />
              </div>
            )}
          </div>

          <div className={styles.row}>
            <div className={styles.field}>
              <Input
                type="tel"
                inputMode="numeric"
                label="Celular"
                placeholder="(00) 90000-0000"
                autoComplete="off"
                maxLength={15}
                errors={errors.phone?.message}
                {...register('phone', {
                  onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                    setValue('phone', maskMobile(event.target.value)),
                })}
              />
            </div>

            <div className={styles.field}>
              <Input
                type="email"
                label="E-mail"
                placeholder="voce@exemplo.com"
                autoComplete="off"
                errors={errors.email?.message}
                {...register('email')}
              />
            </div>
          </div>
        </WizardSection>

        <div className={styles.footerSingle}>
          <Button
            type="submit"
            color="primary"
            text="Simular agora"
            isLoading={isSubmitting}
            disabled={isSubmitting}
          />
        </div>
      </form>
    </WizardShell>
  );
}
